"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModifierService = void 0;
const rounding_lib_1 = require("../rounding/rounding.lib");
const ModifierConditionTypes_1 = require("./types/ModifierConditionTypes");
const ModifierInput_1 = require("./types/ModifierInput");
const lodash_1 = require("lodash");
const ModifierOperationTypes_1 = require("./types/ModifierOperationTypes");
class ModifierService {
    applyModifiers(modifiers, inputDataObject) {
        let cache = {}, originalInputData = Object.assign({}, inputDataObject);
        let applications = [];
        const sortedModifiers = modifiers.sort((a, b) => a.order - b.order);
        sortedModifiers.forEach(modifier => {
            if (this.modifierConditionsSatisfied(modifier, inputDataObject, cache, originalInputData)) {
                const sortedOperations = modifier.operations.sort((a, b) => a.order - b.order);
                sortedOperations.forEach(operation => {
                    applications.push(this.applyOperation(modifier, operation, inputDataObject, cache, originalInputData));
                });
            }
        });
        return applications;
    }
    applyOperation(modifier, operation, baseObject, cache, originalInputData) {
        const originalValue = (0, lodash_1.get)(originalInputData, operation.param), previousValue = (0, lodash_1.get)(baseObject, operation.param);
        const operationInputValueBase = this.reduceInputs(operation.inputs, baseObject, cache, originalInputData);
        switch (operation.type) {
            case 'set':
                (0, lodash_1.set)(baseObject, operation.param, operationInputValueBase);
                break;
            case 'cache':
                (0, lodash_1.set)(cache, operation.param, operationInputValueBase);
                break;
            default:
                const availableTypes = Object.keys(ModifierOperationTypes_1.ModifierOperationTypes).join(', ');
                throw new Error(`Modifier Operation type must be one of ${availableTypes}: ${operation.type} provided`);
        }
        return {
            modifierId: modifier.id,
            operationId: operation.id,
            param: operation.param,
            originalValue,
            previousValue,
            newValue: operationInputValueBase,
        };
    }
    reduceInputs(inputs, baseObject, cache, originalInputData, previous = 0) {
        return inputs.reduce((aggregate, current) => {
            const base = this.getInputValue(current, baseObject, aggregate, cache, originalInputData);
            return current.inputs ? this.reduceInputs(current.inputs, baseObject, cache, originalInputData, base) : base;
        }, previous);
    }
    getInputValue(input, baseObject, aggregateValue, cache, originalInputData) {
        var _a;
        let base = this.getInputBase(input, baseObject, aggregateValue, cache, originalInputData);
        if ((_a = input.meta) === null || _a === void 0 ? void 0 : _a.roundingStrategy) {
            base = this.getValueByRoundingStrategy(input.meta.roundingStrategy, base, input.meta.roundingPrecision);
        }
        if (!!input.operand) {
            return this.getOperandValue(base, input.operand, baseObject, aggregateValue, cache, originalInputData);
        }
        return base;
    }
    getInputBase(input, baseObject, aggregateValue, cache, originalInputData) {
        const path = input.value;
        let result;
        switch (input.base) {
            case 'inputParams':
                result = (0, lodash_1.get)(baseObject, path);
                break;
            case 'cache':
                result = (0, lodash_1.get)(cache, path);
                break;
            case 'originalInputData':
                result = (0, lodash_1.get)(originalInputData, path);
                break;
            case 'aggregate':
                result = aggregateValue;
                break;
            case 'rawValue':
                result = input.value;
                break;
            case 'constant':
                result = this.getConstantValue(input.value);
                break;
            case 'modelGetter':
                result = this.callModelGetterByName(baseObject, input.value);
                break;
            case 'fromValueSet':
                result = this.getInputValueFromSet(input, baseObject, aggregateValue, cache, originalInputData);
                break;
            default:
                const validTypes = Object.keys(ModifierInput_1.ModifierOperandTypes).join(', ');
                throw Error(`Modifier input base must be one of (${validTypes}): ${input.base} provided`);
        }
        return result;
    }
    getOperandValue(baseValue, operand, baseObject, aggregateValue, cache, originalInputData) {
        var _a, _b;
        const operandValue = this.getInputBase(operand, baseObject, aggregateValue, cache, originalInputData);
        let result;
        switch (operand.type) {
            case 'add':
                result = baseValue + operandValue;
                break;
            case 'subtract':
                result = baseValue - operandValue;
                break;
            case 'multiply':
                result = baseValue * operandValue;
                break;
            case 'divide':
                result = baseValue / operandValue;
                break;
            case 'power':
                result = Math.pow(baseValue, operandValue);
                break;
            default:
                const validTypes = Object.keys(ModifierInput_1.ModifierOperandTypes).join(', ');
                throw new Error(`Modifier Operation type must be one of (${validTypes}): ${operand.type} provided`);
        }
        if ((_a = operand.meta) === null || _a === void 0 ? void 0 : _a.roundingStrategy) {
            const precision = ((_b = operand.meta) === null || _b === void 0 ? void 0 : _b.roundingPrecision) || 2;
            result = this.getValueByRoundingStrategy(operand.meta.roundingStrategy, result, precision);
        }
        //check for composite operand or return result
        return operand.operand
            ? this.getOperandValue(result, operand.operand, baseObject, aggregateValue, cache, originalInputData)
            : result;
    }
    modifierConditionsSatisfied(modifier, baseObject, cache, originalInputData) {
        const conditionSets = modifier.conditionSets || [];
        return !conditionSets.find((set) => !this.conditionSetSatisfied(set, baseObject, cache, originalInputData));
    }
    conditionSetSatisfied(conditionSet, baseObject, cache, originalInputData) {
        const { conditions } = conditionSet;
        switch (conditionSet.type) {
            case 'any':
                return !!conditions.find(condition => this.paramValueMeetsCondition(condition, baseObject, cache, originalInputData));
            case 'all':
                return !conditions.find(condition => !this.paramValueMeetsCondition(condition, baseObject, cache, originalInputData));
            case 'none':
                return !conditions.find(condition => this.paramValueMeetsCondition(condition, baseObject, cache, originalInputData));
            default:
                const validTypes = Object.keys(ModifierConditionTypes_1.ModifierConditionSetTypes).join(', ');
                throw new Error(`Modifier condition set type must be one of (${validTypes}): ${conditionSet.type} provided.`);
        }
    }
    paramValueMeetsCondition(condition, baseObject, cache, originalInputData) {
        const baseValue = this.reduceInputs(condition.baseInputs, baseObject, cache, originalInputData);
        const compareValue = this.reduceInputs(condition.compareInputs, baseObject, cache, originalInputData);
        switch (condition.type) {
            case 'exists':
                return !!baseValue;
            case 'notExists':
                return !baseValue;
            case 'equals':
                return baseValue === compareValue;
            case 'notEquals':
                return baseValue !== compareValue;
            case 'greaterThan':
                return baseValue > compareValue;
            case 'lesserThan':
                return baseValue < compareValue;
            case 'gte':
                return baseValue >= compareValue;
            case 'lte':
                return baseValue <= compareValue;
            default:
                const validTypes = Object.keys(ModifierConditionTypes_1.ModifierConditionTypes).join(', ');
                throw new Error(`Modifier condition types must be one of (${validTypes}): ${condition.type} provided`);
        }
    }
    getValueByRoundingStrategy(roundingStrategy, base, precision = 2) {
        switch (roundingStrategy) {
            case 'floorLowerHundred':
                return (0, rounding_lib_1.floorLowerHundred)(base);
            case 'halfEvenClosestDollar':
                return (0, rounding_lib_1.roundHalfEven)(base);
            case 'fixed':
                return (0, rounding_lib_1.toFixedNumber)(base, precision, 10);
            case 'floor':
                return Math.floor(base);
            case 'ceil':
                return Math.ceil(base);
            case 'round':
                return Math.round(base);
            case 'halfEven':
            default:
                return (0, rounding_lib_1.roundHalfEven)(base / 100) * 100;
        }
    }
    getConstantValue(constantName) {
        switch (constantName) {
            case 'currentYear':
                return new Date().getFullYear();
            default:
                throw new Error(`Invalid constant name: ${constantName} in ModifierService@getConstantValue`);
        }
    }
    callModelGetterByName(inputDataObject, methodName) {
        const method = (0, lodash_1.get)(inputDataObject, methodName);
        if ((0, lodash_1.isFunction)(method)) {
            return method.call(inputDataObject);
        }
    }
    getInputValueFromSet(input, baseObject, aggregateValue, cache, originalInputData) {
        if (!input.valueSet) {
            throw new Error(`Input has no valueSet for input with base type fromValueSet in ModifierService@getInputValueFromSet`);
        }
        if (!input.valueSetType) {
            throw new Error(`Input has no valueSetType for input with base type fromValueSet in ModifierService@getInputValueFromSet`);
        }
        const values = input.valueSet.map(setInput => {
            return this.getInputValue(setInput, baseObject, aggregateValue, cache, originalInputData);
        });
        switch (input.valueSetType) {
            case 'min':
                return (0, lodash_1.min)(values);
            case 'max':
                return (0, lodash_1.max)(values);
            default:
                throw new Error(`Invalid valueSetType: ${input.valueSetType} in ModifierService@getInputValueFromSet`);
        }
    }
}
exports.ModifierService = ModifierService;
